// Resolve-page source + media controls (2026-08-18):
//
//   <RemindAssigneeButton issue me={{id,email}} onChanged />   top bar - emails the
//                                   assignee / responsibles a please-complete nudge;
//                                   hidden when the viewer IS the assignee.
//   <IssueSourceBar issue onChanged />   bar above the primary pane (took the place
//                                   of ASSIGNED TO, which moved to the top bar):
//       SHEET  [select w/ PDF previews of the project's doc-control sheets]
//              [Import sheet/image]  [Paste media]
//   <IssueAttachmentsStrip issue canEdit me onChanged />  media attached to the issue
//                                   (image / video / PDF / Excel-CSV) with previews.
//
// Server: GET /issue/:id/sheets, PUT /issue/:id/sheet, POST /issue/:id/sheet/upload,
// POST/DELETE /issue/:id/attachments, POST /issue/:id/remind (routes/issue.js).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Popconfirm, Select, Tooltip, message } from "antd";
import {
  BellOutlined,
  UploadOutlined,
  SnippetsOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  FileOutlined,
  DeleteOutlined,
  InboxOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { T } from "pages/Issue/theme";
import {
  getIssueSheets,
  setIssueSheet,
  uploadIssueSheet,
  uploadIssueAttachments,
  deleteIssueAttachment,
  remindIssue,
  getIssueModelSources,
  setIssueSourceModel,
} from "api/issue/issueApi";
import { absoluteDocumentUrl, renderPdfThumbnail } from "helpers/pdfPreview";
import usePermissions from "hooks/usePermissions";
import { url, tokenRef } from "api";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import { issueLabel } from "helpers/artifactPrefix";
import { HoursSpentField, validHours, askHoursSpent } from "./HoursSpent";

const idOf = (v) => (v && typeof v === "object" ? String(v._id || v.id || "") : (v ? String(v) : ""));
const lower = (s) => String(s || "").trim().toLowerCase();

// ---- media kinds ---------------------------------------------------------
// Mirrors back/utils/mediaKinds.js - the server judges by name as well
// as mime, because a DWG arrives as application/octet-stream.
export const MEDIA_ACCEPT = "image/*,video/*,.pdf,application/pdf,.xls,.xlsx,.xlsm,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,.dwg,.dxf,.dwf,.dwfx,.doc,.docx,.rtf,.txt";
export const MEDIA_ALLOWED_TEXT = "images, video, PDF, Excel/CSV, DWG/DXF and Word files";
export function mediaKindOf(file) {
  const m = lower(file?.type), n = lower(file?.name);
  if (/^image\//.test(m) || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(n)) return "image";
  if (/^video\//.test(m) || /\.(mp4|webm|mov|m4v|ogv)$/.test(n)) return "video";
  if (/pdf$/.test(m) || /\.pdf$/.test(n)) return "pdf";
  if (/(spreadsheet|ms-excel|csv)/.test(m) || /\.(xlsx?|xlsm|csv)$/.test(n)) return "excel";
  if (/(acad|autocad|dwg|dxf|dwf)/.test(m) || /\.(dwg|dxf|dwf|dwfx)$/.test(n)) return "drawing";
  if (/(msword|wordprocessingml|rtf)/.test(m) || /\.(docx?|rtf|txt)$/.test(n)) return "doc";
  return null;
}
// Kind of a stored attachment / evidence row (by its kind field, else its name).
export function storedKindOf(att) {
  const k = lower(att?.kind);
  if (["image", "video", "pdf", "excel", "drawing", "doc"].includes(k)) return k;
  if (k === "document") return "doc";
  return mediaKindOf({ type: att?.mime, name: att?.name || att?.url }) || "file";
}
export const KIND_META = {
  image: { label: "Image", icon: FileImageOutlined, color: "#2f6fed" },
  video: { label: "Video", icon: VideoCameraOutlined, color: "#7b3fe4" },
  pdf: { label: "PDF", icon: FilePdfOutlined, color: "#d64545" },
  excel: { label: "Excel", icon: FileExcelOutlined, color: "#1f8f4e" },
  drawing: { label: "Drawing", icon: FileOutlined, color: "#b45309" },
  doc: { label: "Document", icon: FileOutlined, color: "#475467" },
  sheet: { label: "Sheet", icon: FilePdfOutlined, color: "#1a1a2e" },
  file: { label: "File", icon: FileOutlined, color: "#667085" },
};
const fmtSize = (n) => (!n ? "" : n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

// Signed-in users need issueEdit for the sheet controls (server enforces
// it too); guests carry their own per-issue permission.
function useEditAllowed() {
  const { has, userInfo } = usePermissions();
  if (!userInfo?.id && !userInfo?._id) return true;
  return has(ALL_PERMISSIONS.ISSUE_EDIT);
}

// ---- REMIND ASSIGNEE ------------------------------------------------------
// Assignees = assignedTo + responsibleUsers + no-account responsibleContacts.
// Shown only when there is someone to remind and the viewer is NOT the
// (primary) assignee - reminding yourself makes no sense.
export function issueAssigneeList(issue) {
  const out = [];
  const seen = new Set();
  const push = (p, tag) => {
    const u = p && p.userId !== undefined && p.userId !== null && typeof p.userId === "object" ? p.userId : (p?.userId ? { _id: p.userId } : p);
    if (!u) return;
    const key = idOf(u) || lower(u.email);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({ id: idOf(u), email: lower(u.email), name: [u.name, u.lastName].filter(Boolean).join(" ") || u.email || "", tag });
  };
  if (issue?.assignedTo?.userId) push(issue.assignedTo, "assignee");
  for (const r of issue?.responsibleUsers || []) push(r, "responsible");
  for (const c of issue?.responsibleContacts || []) push({ email: c.email, name: c.name }, "contact");
  return out;
}

export function RemindAssigneeButton({ issue, me, onChanged, size = "small" }) {
  const [sending, setSending] = useState(false);
  const list = useMemo(() => issueAssigneeList(issue), [issue]);
  const meId = idOf(me?.id || me?._id), meEmail = lower(me?.email);
  const primary = list.find((a) => a.tag === "assignee") || null;
  const iAmAssignee = primary
    ? (primary.id && primary.id === meId) || (primary.email && primary.email === meEmail)
    : list.some((a) => (a.id && a.id === meId) || (a.email && a.email === meEmail));
  const others = list.filter((a) => !((a.id && a.id === meId) || (a.email && a.email === meEmail)));
  if (!list.length || iAmAssignee || !others.length) return null;
  const names = others.map((a) => a.name || a.email).filter(Boolean);
  const who = names.length <= 2 ? names.join(" and ") : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  const last = issue?.lastRemindedAt
    ? `Last reminded ${moment(issue.lastRemindedAt).fromNow()}${issue.lastRemindedBy ? ` by ${issue.lastRemindedBy}` : ""}`
    : "";
  const send = async () => {
    setSending(true);
    try {
      const r = await remindIssue(issue._id);
      message.success(`Reminder emailed to ${r.sent} ${r.sent === 1 ? "person" : "people"}`);
      await onChanged?.({ reminded: true });
    } catch (e) {
      message.error(e.message || "Could not send the reminder");
    } finally {
      setSending(false);
    }
  };
  return (
    <Popconfirm
      title="Remind the assignee"
      description={<span>Email a please-complete reminder to <b>{who}</b>?{last ? <><br /><span style={{ color: T.muted, fontSize: 11.5 }}>{last}</span></> : null}</span>}
      okText="Send reminder" cancelText="Cancel" onConfirm={send} okButtonProps={{ loading: sending }}
      placement="bottom"
    >
      <Tooltip title={`Email ${who} a reminder to complete this issue${last ? `. ${last}` : ""}`}>
        <Button size={size} icon={<BellOutlined />} loading={sending}>Remind assignee</Button>
      </Tooltip>
    </Popconfirm>
  );
}

// ---- SHEET SELECT ---------------------------------------------------------
function SheetThumb({ link, width = 64, height = 46, radius = 4 }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setSrc(null); setFailed(false);
    renderPdfThumbnail(link).then((s) => { if (!alive) return; if (s) setSrc(s); else setFailed(true); });
    return () => { alive = false; };
  }, [link]);
  return (
    <div style={{ width, height, borderRadius: radius, overflow: "hidden", background: "#f3f4f5", border: `1px solid ${T.line}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {src ? <img src={src} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        : failed ? <FilePdfOutlined style={{ color: T.sub }} />
          : <span style={{ fontSize: 9, color: T.sub }}>…</span>}
    </div>
  );
}

const sheetLabel = (s) => [s?.sheetNumber, s?.sheetName].filter(Boolean).join(" · ") || s?.revitFileName || "Sheet";

export function IssueSheetSelect({ issue, onChanged, size = "small", style }) {
  const [sheets, setSheets] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const allowed = useEditAllowed();
  const issueId = issue?._id;
  useEffect(() => {
    let alive = true;
    if (!issueId) return undefined;
    setSheets(null);
    getIssueSheets(issueId)
      .then((r) => { if (alive) setSheets(r?.sheets || []); })
      .catch((e) => { if (alive) { setSheets([]); message.error(e.message || "Could not load the project sheets"); } });
    return () => { alive = false; };
  }, [issueId]);

  const currentId = idOf(issue?.sourceDocControlId);
  const currentUrl = issue?.sourceDocument?.url || "";
  const options = useMemo(() => {
    const list = (sheets || []).map((s) => ({
      value: String(s._id),
      title: `${sheetLabel(s)}${typeof s.revisionNumber === "number" ? ` (R${s.revisionNumber})` : ""}`,
      // Everything a person might type to find a sheet: number, name,
      // discipline, level, package, status, source file, revision.
      search: [s.sheetNumber, s.sheetName, s.discipline?.value, s.discipline?.key, s.floorLevel, s.package, s.sheetStatus,
        s.revitFileName, s.docType, typeof s.revisionNumber === "number" ? `r${s.revisionNumber} rev ${s.revisionNumber}` : ""]
        .filter(Boolean).join(" ").toLowerCase(),
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
          {open ? <SheetThumb link={s.link} /> : <div style={{ width: 64, height: 46 }} />}
          <div style={{ minWidth: 0, lineHeight: 1.25 }}>
            <div style={{ fontWeight: 600, color: T.ink, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 300 }}>{sheetLabel(s)}</div>
            <div style={{ fontSize: 11, color: T.muted }}>
              {typeof s.revisionNumber === "number" ? `R${s.revisionNumber}` : ""}
              {s.discipline?.value ? ` · ${s.discipline.value}` : ""}
              {s.revisionDate ? ` · ${moment(s.revisionDate).format("MMM D, YYYY")}` : ""}
            </div>
          </div>
        </div>
      ),
    }));
    // The current source may be an imported one-off file (not in doc
    // control) - surface it so the select isn't blank.
    if (currentUrl && !currentId) {
      list.unshift({ value: "__imported__", title: `${issue?.sourceDocument?.name || "Imported sheet"} (imported)`, search: "imported",
        label: <span style={{ fontSize: 12.5 }}>{issue?.sourceDocument?.name || "Imported sheet"} <span style={{ color: T.muted }}>(imported file)</span></span> });
    }
    if (currentUrl) {
      list.push({ value: "__none__", title: "No sheet", search: "none detach clear",
        label: <span style={{ fontSize: 12.5, color: T.muted }}><CloseOutlined style={{ marginRight: 6 }} />Detach the sheet</span> });
    }
    return list;
  }, [sheets, open, currentUrl, currentId, issue?.sourceDocument?.name]);

  const value = currentId && (sheets || []).some((s) => String(s._id) === currentId)
    ? currentId
    : (currentUrl && !currentId ? "__imported__" : (currentId ? { value: currentId, label: issue?.sourceDocument?.sheetNumber || issue?.sourceDocument?.name || "Sheet" } : undefined));

  const handleChange = async (v) => {
    if (v === "__imported__") return;
    setSaving(true);
    try {
      const r = await setIssueSheet(issue._id, v === "__none__" ? null : v);
      message.success(v === "__none__" ? "Sheet detached" : `Sheet set to ${r?.sourceDocument?.sheetNumber || r?.sourceDocument?.name || "the selected sheet"}`);
      await onChanged?.({ sheet: true, sourceDocument: r?.sourceDocument });
    } catch (e) {
      message.error(e.message || "Could not set the sheet");
    } finally {
      setSaving(false);
    }
  };

  const empty = sheets !== null && (sheets || []).length === 0;
  return (
    <Select
      size={size}
      showSearch
      value={value}
      placeholder={sheets === null ? "Loading sheets…" : (empty ? "No PDF sheets in this project" : "Search sheets by number, name, level, discipline…")}
      options={options}
      optionLabelProp="title"
      onChange={handleChange}
      open={open}
      onDropdownVisibleChange={setOpen}
      loading={saving || sheets === null}
      disabled={saving || !allowed || (empty && !currentUrl)}
      title={allowed ? "Pick a doc-control PDF sheet of this project as the issue's 2D source" : "You do not have the issueEdit permission"}
      style={{ minWidth: 220, width: 300, ...style }}
      dropdownMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 380 }}
      listHeight={320}
      filterOption={(input, option) => String(option?.search || option?.title || "").toLowerCase().includes(input.toLowerCase())}
      notFoundContent={<span style={{ fontSize: 12, color: T.muted }}>{empty ? "This project has no document-control sheets yet - use Import sheet/image" : "No matching sheet"}</span>}
    />
  );
}

// ---- 3D MODEL SOURCE (model pushes / revision model versions / issue exports)
// One select, three groups: the project's own MODEL PUSHES (every sync
// the plugin made - what the coordination side calls a revision), the
// project's model versions (each Timelines export with its Revision -
// every revision loads ITS OWN files) and the model elements exported
// against issues from Revit / Navisworks (this issue's own export
// first). All three render in the streaming viewer.
//
// The pushes lane was missing here long after the endpoint learned to
// return it (listModelSources' `commits`), and its absence was worse
// than a missing option: an issue raised from /analytics/revisions is
// attached to a PUSH, so this select had a value ("commit:<id>") that
// matched none of its options and rendered blank. It read as "the
// revision is not a 3D source" and the obvious repair - pick something
// from the list - detached the push the issue was raised against.
export function IssueModelSourceSelect({ issue, onChanged, size = "small", style }) {
  const [data, setData] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);
  const allowed = useEditAllowed();
  const issueId = issue?._id;
  const reloadKey = `${issue?.sourceModel?.commitId || ""}|${issue?.sourceModel?.timelineId || ""}|${issue?.sourceModel?.fromIssueId || ""}|${(issue?.sourceModel?.zipInfo?.paths || []).length}`;
  useEffect(() => {
    let alive = true;
    if (!issueId) return undefined;
    getIssueModelSources(issueId)
      .then((r) => { if (alive) setData(r || { commits: [], timelines: [], issues: [], current: null }); })
      .catch((e) => { if (alive) { setData({ commits: [], timelines: [], issues: [], current: null }); message.error(e.message || "Could not load the model versions"); } });
    return () => { alive = false; };
  }, [issueId, reloadKey]);

  // A push is only offerable when it still has geometry: its own
  // versioned export, or being the live model. The rest are history the
  // VERSIONS panel lists with the reason - offering them here would only
  // produce the endpoint's 400.
  // Memoised: a fresh array every render would rebuild the option list
  // (and the Select's children) on every keystroke in the search box.
  const commits = useMemo(() => (data?.commits || []).filter((c) => c.streamable), [data]);
  const timelines = data?.timelines || [];
  const issues = data?.issues || [];
  const current = data?.current || null;
  const fmt = (d) => (d ? moment(d).format("MMM D, YYYY") : "");
  // Pushes carry the time as well: a busy day produces several, and
  // "Aug 24" three times over tells a reviewer nothing about which is
  // which.
  const fmtTs = (d) => (d ? moment(d).format("MMM D, YYYY \u00b7 h:mm A") : "");
  const streamTag = (x) => (x.manifestPath ? "streams" : `${x.glbCount || x.fileCount} file${(x.glbCount || x.fileCount) === 1 ? "" : "s"}`);
  const options = useMemo(() => {
    const groups = [];
    if (commits.length) {
      groups.push({
        label: <span style={{ fontSize: 10.5, letterSpacing: ".06em" }}>MODEL PUSHES · SYNCS FROM REVIT / NAVISWORKS</span>,
        options: commits.map((c) => {
          const title = `${c.title}${c.kind === "selection" ? " (selection)" : ""}`;
          const delta = [
            c.counts?.added ? `+${c.counts.added}` : "",
            c.counts?.modified ? `~${c.counts.modified}` : "",
            c.counts?.removed ? `\u2212${c.counts.removed}` : "",
          ].filter(Boolean).join(" ");
          return {
            value: `commit:${c.commitId}`, title,
            search: `${c.title} ${c.by || ""} ${c.narrative || ""} ${fmt(c.ts)} push sync revision`.toLowerCase(),
            label: (
              <div style={{ lineHeight: 1.25, padding: "2px 0" }}>
                <div style={{ fontWeight: 600, color: T.ink, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>
                  {title}{c.live ? <span style={{ marginLeft: 6, fontSize: 10, color: "#2E7D32" }}>LIVE</span> : null}
                </div>
                <div style={{ fontSize: 11, color: T.muted }}>
                  {[fmtTs(c.ts), c.by, delta || (c.hasDiff ? "no change" : ""), "streams"].filter(Boolean).join(" \u00b7 ")}
                </div>
              </div>
            ),
          };
        }),
      });
    }
    if (timelines.length) {
      groups.push({
        label: <span style={{ fontSize: 10.5, letterSpacing: ".06em" }}>MODEL VERSIONS · PROJECT REVISIONS</span>,
        options: timelines.map((t) => {
          const rev = t.revision ? `R${t.revision.revisionNumber}${t.revision.key ? ` ${t.revision.key}` : ""}` : "";
          const title = [rev, t.title].filter(Boolean).join(" · ") || "Model version";
          return {
            value: `timeline:${t._id}`, title, search: `${rev} ${t.title} ${t.milestone || ""} ${fmt(t.date)}`.toLowerCase(),
            label: (
              <div style={{ lineHeight: 1.25, padding: "2px 0" }}>
                <div style={{ fontWeight: 600, color: T.ink, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>{title}</div>
                <div style={{ fontSize: 11, color: T.muted }}>{[fmt(t.date), t.milestone, streamTag(t)].filter(Boolean).join(" · ")}</div>
              </div>
            ),
          };
        }),
      });
    }
    if (issues.length) {
      groups.push({
        label: <span style={{ fontSize: 10.5, letterSpacing: ".06em" }}>ELEMENTS EXPORTED AGAINST ISSUES · REVIT / NAVISWORKS</span>,
        options: issues.map((i) => {
          const title = i.isThisIssue ? `This issue${i.displayId ? ` (${issueLabel(i)})` : ""} - exported elements` : `${issueLabel(i)} · ${i.title}`;
          return {
            value: `issue:${i._id}`, title, search: `mk-${i.displayId} ${i.title} ${i.name || ""}`.toLowerCase(),
            label: (
              <div style={{ lineHeight: 1.25, padding: "2px 0" }}>
                <div style={{ fontWeight: 600, color: T.ink, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>{title}</div>
                <div style={{ fontSize: 11, color: T.muted }}>{[i.name, fmt(i.exportedAt), streamTag(i)].filter(Boolean).join(" · ")}</div>
              </div>
            ),
          };
        }),
      });
    }
    if (current) {
      groups.push({ label: "", options: [{ value: "__none__", title: "No 3D source", search: "none detach clear",
        label: <span style={{ fontSize: 12.5, color: T.muted }}><CloseOutlined style={{ marginRight: 6 }} />Detach the 3D source</span> }] });
    }
    return groups;
  }, [commits, timelines, issues, current]);

  const value = current ? `${current.kind}:${current.id}` : undefined;
  const handleChange = async (v) => {
    setSaving(true);
    try {
      const body = v === "__none__" ? { clear: true }
        : v.startsWith("commit:") ? { commitId: v.slice(7) }
          : v.startsWith("timeline:") ? { timelineId: v.slice(9) }
            : { fromIssueId: v.slice(6) };
      const r = await setIssueSourceModel(issue._id, body);
      message.success(v === "__none__" ? "3D source detached" : `3D source: ${r?.sourceModel?.name || "updated"} - loading in the viewer`);
      await onChanged?.({ sourceModel: true });
    } catch (e) {
      message.error(e.message || "Could not set the 3D source");
    } finally {
      setSaving(false);
    }
  };
  const empty = data !== null && !commits.length && !timelines.length && !issues.length;
  return (
    <Select
      size={size}
      showSearch
      value={value}
      placeholder={data === null ? "Loading model versions…" : (empty ? "No pushes, model versions or exports on this project" : "Select a model push, a model version or an issue export…")}
      options={options}
      optionLabelProp="title"
      onChange={handleChange}
      loading={saving || data === null}
      disabled={saving || !allowed || (empty && !current)}
      title={allowed ? "Load one of the project's model pushes, a revision's model version, or the elements exported against an issue from Revit / Navisworks, in the streaming viewer" : "You do not have the issueEdit permission"}
      style={{ minWidth: 240, width: 320, ...style }}
      dropdownMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 420 }}
      listHeight={340}
      filterOption={(input, option) => String(option?.search || option?.title || "").toLowerCase().includes(input.toLowerCase())}
      notFoundContent={<span style={{ fontSize: 12, color: T.muted }}>{empty ? "Sync or export the model from Revit / Navisworks (a push, a project export, or an export against this issue) to see it here" : "No match"}</span>}
    />
  );
}

// ---- IMPORT SHEET / IMAGE ---------------------------------------------------
// DELIVER a drawing set as the answer to the issue.
//
// A coordination issue usually ends in a drawing: the clash is resolved,
// the sheet is reissued, and the reissued sheet IS the deliverable. That
// was two separate acts - attach a file here, and separately remember to
// import the set into Doc Control - with no link between them and one of
// them regularly skipped.
//
// This does both and closes the issue: the set is split and filed
// against the issue's own project, every sheet becomes resolution
// evidence, and the issue resolves once they are all in. Not before -
// see utils/issueDeliverable on why the close waits for the import.
export function IssueDeliverSheetsButton({ issue, onChanged, size = "small" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const allowed = useEditAllowed();

  const watch = (importId) => new Promise((done) => {
    const tick = async () => {
      try {
        const res = await fetch(`${url}/doc-control/import-set/${importId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
        if (body.status === "done" || body.status === "failed") return done(body);
        setProgress(`${body.pagesDone || 0}/${body.pageCount || "?"}`);
      } catch (e) {
        return done({ status: "failed", error: e.message });
      }
      setTimeout(tick, 2000);
      return undefined;
    };
    tick();
  });

  // One file at a time through the same route: a PDF is a drawing set
  // (split, filed, watched to the end), anything else - the DWG, the
  // Excel, a photo or a video of the built result - is delivered whole
  // and lands in the DELIVERED row at once.
  const deliverOne = async (file, hoursSpent) => {
    const fd = new FormData();
    fd.append("file", file);
    if (hoursSpent) fd.append("hoursSpent", String(hoursSpent));
    const res = await fetch(`${url}/issue/${issue._id}/deliverable`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
      body: fd,
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
    if (!body.importId) {
      return { name: file.name, filed: 1, resolved: !!body.resolved, kind: body.kind };
    }
    message.info(body.message || "Filing the set…");
    const final = await watch(body.importId);
    if (final.status === "failed") throw new Error(final.error || `${file.name} could not be filed, so the issue was left open`);
    return { name: file.name, filed: (final.stats || {}).filed || 0, resolved: !!body.resolveIssue, kind: "set" };
  };

  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    const bad = files.filter((f) => !mediaKindOf(f));
    if (bad.length) { message.error(`${bad.map((f) => f.name).join(", ")}: only PDF sets, ${MEDIA_ALLOWED_TEXT} can be delivered`); return; }
    // Delivering resolves the issue, and a resolve carries the hours spent.
    let hoursSpent = null;
    if (!["resolved", "abandoned", "closed"].includes(String(issue.status || ""))) {
      hoursSpent = await askHoursSpent({ title: "Hours spent on this issue", okText: "Deliver and resolve" });
      if (hoursSpent === null) return;
    }
    setBusy(true);
    setProgress("");
    let sheets = 0, docs = 0, resolved = false;
    try {
      for (const file of files) {
        setProgress(files.length > 1 ? `${file.name}` : "");
        // eslint-disable-next-line no-await-in-loop
        const r = await deliverOne(file, hoursSpent);
        if (r.kind === "set") sheets += r.filed; else docs += 1;
        resolved = resolved || r.resolved;
      }
      const parts = [];
      if (sheets) parts.push(`${sheets} sheet${sheets === 1 ? "" : "s"} filed`);
      if (docs) parts.push(`${docs} document${docs === 1 ? "" : "s"} delivered`);
      message.success(`${parts.join(", ") || "Delivered"}${resolved ? " — issue resolved" : ""}`);
    } catch (err) {
      message.error(err.message || "Delivery failed");
    } finally {
      setBusy(false);
      setProgress("");
      await onChanged?.({ deliverable: true, sheet: true });
    }
  };

  return (
    <>
      <input ref={ref} type="file" multiple accept={MEDIA_ACCEPT} style={{ display: "none" }} onChange={onFiles} />
      <Tooltip title="Deliver the answer to this issue: a reissued drawing set (PDF, split and filed into Doc Control as sheets), or documents of any kind - Excel, DWG, images, video. Each one becomes resolution evidence, shows in the DELIVERED row, and resolves the issue.">
        <Button
          size={size}
          type="primary"
          ghost
          icon={<UploadOutlined />}
          loading={busy}
          disabled={!allowed}
          onClick={() => ref.current && ref.current.click()}
        >
          {busy && progress ? `Filing ${progress}…` : "Deliver documents / sheets"}
        </Button>
      </Tooltip>
    </>
  );
}

export function IssueImportSheetButton({ issue, onChanged, size = "small" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const allowed = useEditAllowed();
  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const kind = mediaKindOf(file);
    if (kind !== "pdf" && kind !== "image") { message.error("A sheet must be a PDF or an image"); return; }
    setBusy(true);
    try {
      await uploadIssueSheet(issue._id, file);
      message.success(`${file.name} is now the issue's sheet`);
      await onChanged?.({ sheet: true });
    } catch (err) {
      message.error(err.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".pdf,application/pdf,image/*" style={{ display: "none" }} onChange={onFile} />
      <Tooltip title="Import a PDF or an image file as this issue's 2D sheet (replaces the current sheet; markup history is kept)">
        <Button size={size} icon={<UploadOutlined />} loading={busy} disabled={!allowed} onClick={() => ref.current && ref.current.click()}>
          Import sheet/image
        </Button>
      </Tooltip>
    </>
  );
}

// ---- PASTE MEDIA -------------------------------------------------------------
// 1) Tries the async clipboard API first (images copied to the clipboard).
// 2) Otherwise opens a modal with a paste/drop zone: pressing Ctrl+V there
//    hands over clipboardData.files - which is the only way a browser
//    exposes files copied from Explorer/Finder (PDF, Excel, video…).
export function readClipboardFiles() {
  if (!navigator.clipboard || !navigator.clipboard.read) return Promise.resolve([]);
  return navigator.clipboard.read().then(async (items) => {
    const files = [];
    for (const item of items) {
      const type = (item.types || []).find((t) => /^(image|video)\//.test(t) || /pdf|spreadsheet|excel|csv/.test(t));
      if (!type) continue;
      try {
        const blob = await item.getType(type);
        const ext = type.split("/")[1]?.split(";")[0] || "bin";
        files.push(new File([blob], `clipboard-${moment().format("YYYYMMDD-HHmmss")}.${ext === "jpeg" ? "jpg" : ext}`, { type }));
      } catch (e) { /* skip */ }
    }
    return files;
  }).catch(() => []);
}

export function IssuePasteMediaButton({ issue, onChanged, size = "small", buttonProps = {} }) {
  const [open, setOpen] = useState(false);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const zoneRef = useRef(null);
  const fileRef = useRef(null);
  const allowed = useEditAllowed();

  const upload = useCallback(async (files) => {
    const ok = [...files].filter((f) => mediaKindOf(f));
    const bad = [...files].filter((f) => !mediaKindOf(f));
    if (bad.length) message.warning(`${bad.length} file(s) skipped - only images, video, PDF and Excel/CSV are accepted`);
    if (!ok.length) return false;
    setBusy(true);
    try {
      const r = await uploadIssueAttachments(issue._id, ok);
      const added = r?.added || [];
      message.success(`${added.length || ok.length} file(s) attached to the issue`);
      // Hand the first file back so the page can put it on the canvas -
      // "Paste media" and Ctrl+V should land in the same place.
      await onChanged?.({ attachments: true, added });
      return true;
    } catch (e) {
      message.error(e.message || "Could not attach the files");
      return false;
    } finally {
      setBusy(false);
    }
  }, [issue?._id, onChanged]);

  const onClick = async () => {
    // Fast path: an image sitting on the clipboard.
    const files = await readClipboardFiles();
    if (files.length) { await upload(files); return; }
    setQueue([]);
    setOpen(true);
    setTimeout(() => zoneRef.current && zoneRef.current.focus(), 50);
  };

  const takeFiles = (list) => {
    const arr = [...(list || [])].filter(Boolean);
    if (!arr.length) return;
    setQueue((q) => [...q, ...arr]);
  };
  const onPaste = (e) => {
    const dt = e.clipboardData;
    if (!dt) return;
    const files = dt.files && dt.files.length ? [...dt.files]
      : [...(dt.items || [])].filter((it) => it.kind === "file").map((it) => it.getAsFile()).filter(Boolean);
    if (files.length) { e.preventDefault(); takeFiles(files); }
    else message.info("Nothing attachable on the clipboard - copy an image, or a file from your file explorer, then paste again");
  };
  const onDrop = (e) => { e.preventDefault(); takeFiles(e.dataTransfer?.files); };
  const removeAt = (i) => setQueue((q) => q.filter((_, j) => j !== i));
  const commit = async () => {
    if (!queue.length) return;
    const ok = await upload(queue);
    if (ok) { setOpen(false); setQueue([]); }
  };

  return (
    <>
      <Tooltip title="Attach media from the clipboard: images, video, PDF, Excel/CSV">
        <Button size={size} icon={<SnippetsOutlined />} loading={busy && !open} disabled={!allowed} onClick={onClick} {...buttonProps}>
          Paste media
        </Button>
      </Tooltip>
      <Modal
        open={open}
        title="Paste media from the clipboard"
        onCancel={() => { setOpen(false); setQueue([]); }}
        onOk={commit}
        okText={queue.length ? `Attach ${queue.length} file${queue.length > 1 ? "s" : ""}` : "Attach"}
        okButtonProps={{ disabled: !queue.length, loading: busy }}
        width={560}
        destroyOnClose
      >
        <div
          ref={zoneRef}
          tabIndex={0}
          onPaste={onPaste}
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => zoneRef.current && zoneRef.current.focus()}
          style={{
            border: `2px dashed ${T.navy}`, borderRadius: 8, padding: "22px 16px", textAlign: "center",
            background: "#f7f9fc", outline: "none", cursor: "text",
          }}
        >
          <InboxOutlined style={{ fontSize: 28, color: T.navy }} />
          <div style={{ fontWeight: 600, color: T.ink, marginTop: 6 }}>Click here, then press Ctrl+V (Cmd+V on Mac)</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
            Images, video, PDF and Excel/CSV files - copied from a screenshot tool, a web page or your file explorer. You can also drop files here.
          </div>
          <div style={{ marginTop: 10 }}>
            <input ref={fileRef} type="file" multiple accept={MEDIA_ACCEPT} style={{ display: "none" }}
              onChange={(e) => { takeFiles(e.target.files); e.target.value = ""; }} />
            <Button size="small" onClick={(e) => { e.stopPropagation(); fileRef.current && fileRef.current.click(); }}>…or choose files</Button>
          </div>
        </div>
        {queue.length > 0 && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflow: "auto" }}>
            {queue.map((f, i) => {
              const k = mediaKindOf(f) || "file";
              const Icon = KIND_META[k].icon;
              const isImg = k === "image";
              return (
                <div key={`${f.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, border: `1px solid ${T.line}`, borderRadius: 6, padding: "6px 8px" }}>
                  {isImg ? <img src={URL.createObjectURL(f)} alt="" style={{ width: 44, height: 34, objectFit: "cover", borderRadius: 4 }} />
                    : <Icon style={{ fontSize: 22, color: KIND_META[k].color, width: 44, textAlign: "center" }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name || `pasted ${KIND_META[k].label.toLowerCase()}`}</div>
                    <div style={{ fontSize: 11, color: T.muted }}>{KIND_META[k].label}{f.size ? ` · ${fmtSize(f.size)}` : ""}</div>
                  </div>
                  <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => removeAt(i)} />
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </>
  );
}

// ---- DOWNLOAD NAMES ---------------------------------------------------------------
// A stored file is named for the store ("issue-6ab5…-1790…-57e0.pdf"),
// and that is what Save used to call it. A file that came in with a real
// name keeps it; a pasted/clipboard one ("image.png", "blob") or one with
// only the stored name is called after the issue instead. The server
// sends ?name= as the file name (back/utils/downloadName.js).
const GENERIC_NAME = /^(?:image|img|blob|file|download|untitled|screenshot|screen shot|clipboard|pasted?|photo|document|scan)(?:[\s_-]*\(?\d*\)?)?(?:\.[a-z0-9]+)?$/i;
const STORED_NAME = /^(?:issue-[0-9a-f]{24}-\d+-[0-9a-f]+|[0-9a-f]{16,})(?:\.[a-z0-9]+)?$/i;

export function hasOwnFileName(name) {
  const s = String(name || "").trim();
  return !!s && !GENERIC_NAME.test(s) && !STORED_NAME.test(s);
}

export function downloadNameFor(entry, issue, index = 0) {
  if (hasOwnFileName(entry?.name)) return String(entry.name).trim();
  const title = String(issue?.title || "").trim();
  if (!title) return "";
  return index > 0 ? `${title} (${index + 1})` : title;
}

// Position among the entries that take the issue's name, so the second
// pasted image is "<title> (2)" whatever sits before it in the list.
export function unnamedIndex(list, i) {
  let n = 0;
  for (let j = 0; j < i; j += 1) if (!hasOwnFileName(list[j]?.name)) n += 1;
  return n;
}

export function namedHref(entry, issue, index = 0) {
  const href = absoluteDocumentUrl(entry?.url);
  const name = downloadNameFor(entry, issue, index);
  if (!href || !name || !/\/uploads\//.test(href)) return href;
  return `${href}${href.includes("?") ? "&" : "?"}name=${encodeURIComponent(name)}`;
}

// ---- RESOLUTION EVIDENCE ---------------------------------------------------------
// Compact list of resolution evidence entries ({url,name,kind}) - used on
// the resolve page (after resolution) and the Review Center cards.
export function EvidenceList({ items, title = "RESOLUTION EVIDENCE", compact = false, issue = null }) {
  const list = (items || []).filter((e) => e && e.url);
  if (!list.length) return null;
  return (
    <div style={{ marginTop: compact ? 6 : 10 }}>
      {title ? (
        <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 4 }}>
          {title} ({list.length})
        </div>
      ) : null}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {list.map((e, i) => {
          const k = e.kind || "image";
          const meta = KIND_META[k] || KIND_META.file;
          const Icon = meta.icon;
          const href = absoluteDocumentUrl(e.url);
          const name = e.name || (k === "image" ? `Screenshot ${i + 1}` : meta.label);
          return (
            <a key={`${e.url}-${i}`} href={namedHref(e, issue, unnamedIndex(list, i))} target="_blank" rel="noopener noreferrer"
              title={`${name}${e.docControlId ? " - filed in Document Control" : ""}`}
              style={{ display: "flex", alignItems: "center", gap: 6, border: `1px solid ${T.line}`, borderRadius: 6, padding: "3px 6px 3px 3px", background: "#fff", color: "inherit", textDecoration: "none", maxWidth: 200 }}>
              {k === "image"
                ? <img src={href} alt="" style={{ width: 40, height: 30, objectFit: "cover", borderRadius: 3 }} />
                : <Icon style={{ fontSize: 18, color: meta.color, width: 40, textAlign: "center" }} />}
              <span style={{ fontSize: 11, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

// Mark-resolved dialog: attach the resolution screenshot(s) / document(s)
// that prove the fix (filed in Document Control under the issue's project,
// "Issues" category, as "Resolution screenshot / document") plus a note.
// onSubmit({ files, notes }) does the upload + proposal submit.
// `required` - the issue's evidence policy set at creation:
//   { screenshot, document, modelPush, pluginResolve } booleans, plus
//   `have` { screenshot, document, modelPush } describing what is already on
//   the issue. Submit stays disabled until every required item is covered by
//   what is on the issue or queued here; pluginResolve can never be satisfied
//   from the web (the plugin must push it) and blocks with an explanation.
const DOC_FILE_RE = /\.(pdf|xlsx?|xlsm|csv|docx?|rtf|txt|dwg|dxf|dwfx?)$/i;
export function ResolveEvidenceModal({ open, onCancel, onSubmit, busy, currentScreenshot, initialNotes = "", gated = false, requireUpload = false, required = null, have = {} }) {
  const [queue, setQueue] = useState([]);
  const [notes, setNotes] = useState(initialNotes || "");
  // Hours spent come with every resolution (components/Issue/HoursSpent).
  const [hours, setHours] = useState(null);
  const fileRef = useRef(null);
  const zoneRef = useRef(null);
  useEffect(() => { if (open) { setQueue([]); setNotes(initialNotes || ""); setHours(null); } }, [open, initialNotes]);
  const takeFiles = (fl) => {
    const ok = [...(fl || [])].filter((f) => mediaKindOf(f));
    const bad = [...(fl || [])].length - ok.length;
    if (bad > 0) message.warning(`${bad} file${bad > 1 ? "s" : ""} skipped (${MEDIA_ALLOWED_TEXT} only)`);
    if (ok.length) setQueue((q) => [...q, ...ok]);
  };
  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    const files = [];
    for (const it of items) { if (it.kind === "file") { const f = it.getAsFile(); if (f) files.push(f); } }
    if (files.length) { e.preventDefault(); takeFiles(files); }
  };
  const onDrop = (e) => { e.preventDefault(); takeFiles(e.dataTransfer?.files); };
  const removeAt = (i) => setQueue((q) => q.filter((_, j) => j !== i));
  const req = required || {};
  const queuedImage = queue.some((f) => /^image\//.test(f.type || "") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(f.name || ""));
  const queuedDoc = queue.some((f) => DOC_FILE_RE.test(f.name || "") || /pdf|spreadsheet|excel|csv|msword|wordprocessing/i.test(f.type || ""));
  const checks = [];
  // A spreadsheet or a PDF shows the progress as well as a picture does:
  // the "screenshot" demand is on every issue by default, and it used
  // to grey this button out for an Excel-only resolution even though the
  // file had uploaded fine (the server accepts a document too).
  if (req.screenshot !== false) checks.push({ key: "screenshot", label: "Screenshot or document of the progress", ok: queuedImage || queuedDoc || !!have.screenshot || !!have.document || (!requireUpload && !!currentScreenshot), hint: "Paste / drop an image, PDF, Excel/CSV or DWG here, or it is already on the issue." });
  if (req.document) checks.push({ key: "document", label: "Uploaded document (PDF / Excel / CSV)", ok: queuedDoc || !!have.document, hint: "Drop a PDF, Excel/CSV sheet or Word file here." });
  if (req.modelPush) checks.push({ key: "modelPush", label: "Model push / diff from Revit or Navisworks", ok: !!have.modelPush, hint: "Push the model from the BidLight Revit / Navisworks plugin so a commit is linked to this issue." });
  if (req.pluginResolve) checks.push({ key: "pluginResolve", label: "Resolution pushed from the Revit / Navisworks plugin", ok: false, hint: "This issue can only be resolved from the plugin - open it in Revit / Navisworks and push the resolution from there." });
  const policyOk = checks.every((c) => c.ok);
  const canGo = !busy && policyOk && validHours(hours) && (queue.length > 0 || (!requireUpload && !!currentScreenshot));
  const shotHref = currentScreenshot ? absoluteDocumentUrl(currentScreenshot) : "";
  return (
    <Modal
      open={open}
      title={gated ? "Submit resolution for review" : "Mark issue resolved"}
      onCancel={onCancel}
      onOk={() => onSubmit && onSubmit({ files: queue, notes, hoursSpent: Number(hours) })}
      okText={busy ? "Saving…" : gated ? "Submit for review" : "Mark resolved"}
      okButtonProps={{ disabled: !canGo, loading: busy }}
      width={600}
      destroyOnClose
    >
      <div style={{ fontSize: 12.5, color: T.text, marginBottom: 10 }}>
        Attach the <b>resolution screenshot or document</b> that shows the fix (photo, marked-up image, PDF, Excel/CSV, video).
        It is saved on the issue and filed in <b>Document Control</b> (Issues category of this project) as resolution evidence.
      </div>
      <div
        ref={zoneRef}
        tabIndex={0}
        onPaste={onPaste}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => zoneRef.current && zoneRef.current.focus()}
        style={{ border: `2px dashed ${T.navy}`, borderRadius: 8, padding: "18px 16px", textAlign: "center", background: "#f7f9fc", outline: "none", cursor: "text" }}
      >
        <InboxOutlined style={{ fontSize: 26, color: T.navy }} />
        <div style={{ fontWeight: 600, color: T.ink, marginTop: 6 }}>Drop files here, or click and press Ctrl+V (Cmd+V on Mac)</div>
        <div style={{ marginTop: 8 }}>
          <input ref={fileRef} type="file" multiple accept={MEDIA_ACCEPT} style={{ display: "none" }}
            onChange={(e) => { takeFiles(e.target.files); e.target.value = ""; }} />
          <Button size="small" icon={<UploadOutlined />} onClick={(e) => { e.stopPropagation(); fileRef.current && fileRef.current.click(); }}>Choose files</Button>
        </div>
      </div>
      {queue.length > 0 && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflow: "auto" }}>
          {queue.map((f, i) => {
            const k = mediaKindOf(f) || "file";
            const Icon = KIND_META[k].icon;
            return (
              <div key={`${f.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, border: `1px solid ${T.line}`, borderRadius: 6, padding: "6px 8px" }}>
                {k === "image" ? <img src={URL.createObjectURL(f)} alt="" style={{ width: 44, height: 34, objectFit: "cover", borderRadius: 4 }} />
                  : <Icon style={{ fontSize: 22, color: KIND_META[k].color, width: 44, textAlign: "center" }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name || `pasted ${KIND_META[k].label.toLowerCase()}`}</div>
                  <div style={{ fontSize: 11, color: T.muted }}>{KIND_META[k].label}{f.size ? ` · ${fmtSize(f.size)}` : ""}</div>
                </div>
                <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => removeAt(i)} />
              </div>
            );
          })}
        </div>
      )}
      {checks.length > 0 && (
        <div style={{ marginTop: 10, border: `1px solid ${T.line}`, borderRadius: 6, padding: "6px 10px" }}>
          <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 2 }}>REQUIRED BY THIS ISSUE</div>
          {checks.map((c) => (
            <div key={c.key} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "3px 0", fontSize: 12 }}>
              <span style={{ width: 16, height: 16, borderRadius: "50%", background: c.ok ? "#2e9e5b" : "#d9822b", color: "#fff", fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{c.ok ? "✓" : "!"}</span>
              <span>
                <b style={{ color: c.ok ? T.ink : "#B84B0A" }}>{c.label}</b>
                {!c.ok && <span style={{ color: T.muted }}> - {c.hint}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
      {!queue.length && currentScreenshot && !requireUpload && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: T.muted, border: `1px solid ${T.line}`, borderRadius: 6, padding: "6px 8px" }}>
          <img src={shotHref} alt="" style={{ width: 56, height: 40, objectFit: "cover", borderRadius: 4 }} />
          <span>No file attached yet - the issue's current image (latest markup) will be recorded as the resolution screenshot.</span>
        </div>
      )}
      <HoursSpentField value={hours} onChange={setHours} />
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 4 }}>RESOLUTION NOTE</div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What was done to resolve this issue? (optional)"
          rows={3}
          style={{ width: "100%", border: `1px solid ${T.line}`, borderRadius: 6, padding: "6px 8px", fontSize: 12.5, resize: "vertical" }}
        />
      </div>
    </Modal>
  );
}

// ---- ATTACHMENTS STRIP ---------------------------------------------------------
function AttachmentCard({ att, canDelete, onDelete, issue, index = 0 }) {
  const k = att.kind || "file";
  const meta = KIND_META[k] || KIND_META.file;
  const Icon = meta.icon;
  const href = absoluteDocumentUrl(att.url);
  const [pdfThumb, setPdfThumb] = useState(null);
  useEffect(() => {
    let alive = true;
    if (k === "pdf") renderPdfThumbnail(att.url).then((s) => { if (alive && s) setPdfThumb(s); });
    return () => { alive = false; };
  }, [k, att.url]);
  const [deleting, setDeleting] = useState(false);
  const who = att.uploaderName ? `${att.uploaderName} · ` : "";
  const when = att.createdAt ? moment(att.createdAt).format("MMM D, HH:mm") : "";
  return (
    <div title={`${att.name || meta.label}${att.size ? ` (${fmtSize(att.size)})` : ""}\n${who}${when}\nClick to open`}
      style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px 4px 4px", borderRadius: 8, border: `1px solid ${T.line}`, background: "#fff", flexShrink: 0, position: "relative" }}>
      {att.role === "resolution" && (
        <span title="Resolution evidence - filed in Document Control" style={{ position: "absolute", top: -7, left: 6, fontSize: 9, fontWeight: 700, letterSpacing: ".05em", padding: "1px 5px", borderRadius: 6, background: T.added2 || "#1f8f4e", color: "#fff", lineHeight: "12px" }}>
          RESOLUTION
        </span>
      )}
      <a href={namedHref(att, issue, index)} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, color: "inherit", textDecoration: "none" }}>
        <div style={{ width: 64, height: 44, borderRadius: 4, overflow: "hidden", background: "#f3f4f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {k === "image" ? <img src={href} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }} />
            : k === "video" ? <video src={href} muted preload="metadata" style={{ maxWidth: "100%", maxHeight: "100%" }} />
              : k === "pdf" && pdfThumb ? <img src={pdfThumb} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                : <Icon style={{ fontSize: 22, color: meta.color }} />}
        </div>
        <div style={{ fontSize: 11.5, lineHeight: 1.25, maxWidth: 150 }}>
          <div style={{ fontWeight: 700, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{att.name || meta.label}</div>
          <div style={{ color: T.text }}>{meta.label}{att.size ? ` · ${fmtSize(att.size)}` : ""}</div>
          <div style={{ color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{who}{when}</div>
        </div>
      </a>
      {canDelete && (
        <Popconfirm title={`Remove ${att.name || "this attachment"}?`} okText="Remove" okButtonProps={{ danger: true }} onConfirm={async () => {
          setDeleting(true);
          try { await onDelete(att); } finally { setDeleting(false); }
        }}>
          <Button size="small" type="text" danger icon={<DeleteOutlined />} loading={deleting} style={{ marginLeft: 2 }} />
        </Popconfirm>
      )}
    </div>
  );
}

export function IssueAttachmentsStrip({ issue, canEdit, me, guest, onChanged, right }) {
  const list = (issue?.attachments || []).slice().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  const meId = idOf(me?.id || me?._id);
  const onDelete = async (att) => {
    try {
      await deleteIssueAttachment(issue._id, att._id);
      message.success("Attachment removed");
      await onChanged?.({ attachments: true });
    } catch (e) {
      message.error(e.message || "Could not remove the attachment");
    }
  };
  if (!list.length) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 24px", background: "#fff", borderBottom: `1px solid ${T.line}`, overflowX: "auto" }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", whiteSpace: "nowrap" }}>
        ATTACHMENTS ({list.length})
      </div>
      {list.map((att, i) => {
        const mine = (meId && String(att.userId?._id || att.userId) === meId) || (guest && att.guestId && String(att.guestId) === String(guest._id));
        return <AttachmentCard key={att._id || att.url} att={att} issue={issue} index={unnamedIndex(list, i)} canDelete={!!canEdit && (!!meId || mine)} onDelete={onDelete} />;
      })}
      {right ? <span style={{ marginLeft: "auto" }}>{right}</span> : null}
    </div>
  );
}

// Page-level Ctrl+V handler helper (resolve page): attach non-image files
// that arrived on the clipboard. Returns the number attached.
// Returns the attachments the server actually stored, so the caller can
// put the first of them on the canvas - which is where a pasted file
// belongs. (It used to return a count, and the file went nowhere the
// user could see it except a chip in the strip.)
export async function uploadPastedFiles(issueId, files) {
  const ok = [...(files || [])].filter((f) => mediaKindOf(f));
  if (!ok.length) return [];
  const r = await uploadIssueAttachments(issueId, ok);
  const added = r?.added || [];
  const n = added.length || ok.length;
  message.success(`${n} file${n > 1 ? "s" : ""} attached to the issue`);
  return added;
}

// ---- SOURCE BAR (above the primary pane) --------------------------------------
// ---- ATTACHED SHEETS -----------------------------------------------------
// An issue can be raised against several sheets at once, but only one can
// be the markup surface (each shape is stored against a page of the sheet
// on screen). The extra picks used to be dropped on the floor at create
// time; they are kept on the issue now, and this is where they surface -
// one chip per sheet, the open one highlighted, click to switch.
export function IssueSheetStrip({ issue, onChanged }) {
  const allowed = useEditAllowed();
  const [busy, setBusy] = useState("");
  const list = (issue?.sourceDocuments || []).filter((d) => d && d.url);
  if (list.length < 2) return null;
  const activeId = idOf(issue?.sourceDocControlId);
  const activeUrl = issue?.sourceDocument?.url || "";
  const isActive = (d) => (d.docControlId && activeId
    ? String(d.docControlId) === activeId
    : String(d.url) === String(activeUrl));

  const pick = async (d) => {
    if (isActive(d) || !d.docControlId) return;
    const key = String(d.docControlId);
    setBusy(key);
    try {
      const r = await setIssueSheet(issue._id, key);
      await onChanged?.({ sheet: true, sourceDocument: r?.sourceDocument });
    } catch (e) {
      message.error(e.message || "Could not open that sheet");
    } finally {
      setBusy("");
    }
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
      background: "#fff", border: `1px solid ${T.line}`, borderRadius: 6,
      padding: "6px 10px", marginBottom: 8, flexShrink: 0,
    }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
        ATTACHED SHEETS ({list.length})
      </span>
      {list.map((d, i) => {
        const active = isActive(d);
        const label = d.sheetNumber || d.name || `Sheet ${i + 1}`;
        return (
          <Tooltip key={`${d.docControlId || d.url}-${i}`} title={active ? "Open for markup" : (allowed && d.docControlId ? "Open this sheet for markup" : label)}>
            <button
              type="button"
              onClick={() => (allowed ? pick(d) : undefined)}
              disabled={!!busy || (!allowed && !active)}
              style={{
                fontSize: 11.5, fontWeight: active ? 700 : 500,
                color: active ? "#fff" : T.navyD,
                background: active ? T.navy : "#fff",
                border: `1px solid ${active ? T.navy : T.line2}`,
                borderRadius: 4, padding: "3px 9px",
                cursor: active || !allowed || !d.docControlId ? "default" : "pointer",
                opacity: busy && busy !== String(d.docControlId) ? 0.5 : 1,
                maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >
              {label}
              {typeof d.revisionNumber === "number" ? ` · R${d.revisionNumber}` : ""}
            </button>
          </Tooltip>
        );
      })}
      <span style={{ fontSize: 10.5, color: T.sub }}>markup belongs to the sheet it was drawn on</span>
    </div>
  );
}

export function IssueSourceBar({ issue, onChanged }) {
  const labelStyle = { fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" };
  const divider = <span style={{ width: 1, height: 20, background: T.line }} />;
  return (
    <>
      <div style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      background: "#fff", border: `1px solid ${T.line}`, borderRadius: 6,
      padding: "6px 10px", marginBottom: 8, flexShrink: 0,
    }}>
      <span style={labelStyle}>SHEET</span>
      <IssueSheetSelect issue={issue} onChanged={onChanged} />
      {divider}
      <span style={labelStyle}>3D MODEL</span>
      <IssueModelSourceSelect issue={issue} onChanged={onChanged} />
      {divider}
      <IssueImportSheetButton issue={issue} onChanged={onChanged} />
      <IssueDeliverSheetsButton issue={issue} onChanged={onChanged} />
      <IssuePasteMediaButton issue={issue} onChanged={onChanged} />
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 10.5, color: T.sub }}>Ctrl+V anywhere: an image becomes the new issue image, other files attach</span>
      </div>
      <IssueSheetStrip issue={issue} onChanged={onChanged} />
    </>
  );
}

export default IssueSourceBar;
